import { FC, memo, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { useConvenienceDispatchDeliveriesQuery } from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { DateModel } from 'src/models/DateModel';
import { normalizePlanningEndOn } from 'src/utils/planningDateRange.util';

import LongHaulPresenter from './Presenter';
import { endOnForSpan, LONG_HAUL_DEFAULT_SPAN_DAYS } from './spanDays';

// 全ページを並列で取り切る。値の意味はカード配車の index.tsx のコメント参照
const PER_PAGE_DELIVERIES = 20;
const PER_PAGE_LEG_RUNS = 200;

/**
 * 'YYYY-MM-DD' → 開始日 + (LONG_HAUL_DEFAULT_SPAN_DAYS - 1) 日。読めない値はそのまま返す。
 *
 * 日付の足し算も既定日数も `spanDays.ts` が持つ（ツールバーの 1日/3日/週（7日） と同じ規則に
 * するため、かつ画面を開く導線と既定日数を 1 か所で揃えるため）。上限は
 * MAX_PLANNING_RANGE_DAYS（10 日）。buildDisplayDays は両端を含むので、7 日表示の終了日は
 * 開始日 + 6 日。
 */
const defaultEndOn = (startOn: string): string => endOnForSpan(startOn, LONG_HAUL_DEFAULT_SPAN_DAYS);

const LongHaul: FC = memo(() => {
  const { startOn: startOnParam, endOn: endOnParam } = useParams<{ startOn: string; endOn: string }>();
  const navigate = useNavigate();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);

  // path param が無い / 開始日が日付として不正な場合は既定日から 7 日へ、
  // レンジが不正（終了日 < 開始日・上限日数超過）な場合は終了日を開始日へ揃えてリダイレクトする。
  // 判定そのものは ConvenienceDispatches / V3Plans / V3Timeline と同じものを通す。
  useEffect(() => {
    if (!startOnParam || !endOnParam || new DateModel(startOnParam).isInValid()) {
      const on =
        licenseContext.config?.on ??
        localStorage.getItem('planningOn') ??
        datetimeDecorator.toYyyyMmDd(new Date());
      void navigate(`/long-haul/${on}/${defaultEndOn(on)}`, { replace: true });
      return;
    }

    const { endOn: normalizedEndOn, changed } = normalizePlanningEndOn(startOnParam, endOnParam);
    if (changed) {
      void navigate(`/long-haul/${startOnParam}/${normalizedEndOn}`, { replace: true });
    }
  }, [startOnParam, endOnParam, licenseContext.config?.on, navigate]);

  const startOn = startOnParam ?? '';
  const endOn = endOnParam ?? startOn;

  // 他の配車画面と同様、現在の期間を plansContext / licenseContext に反映する
  // （ヘッダの画面間リンクがこの値で URL を組む）
  useEffect(() => {
    if (!startOn || !endOn) return;
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    licenseContext.setStartEndOn({ startOn, endOn });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOn, endOn]);

  // 勤務と運行
  const deliveriesQuery = useConvenienceDispatchDeliveriesQuery(startOn, endOn, PER_PAGE_DELIVERIES);
  const legRunsQuery = useInfiniteQueryShipmentOrderLegRuns(startOn, endOn, PER_PAGE_LEG_RUNS);

  const trucksQuery = useQueryPlanningsTrucks(startOn, endOn);
  const driversQuery = useQueryPlanningsDrivers(startOn, endOn);

  // 未割当リストの 32 列フィルタ向けの選択肢。Map/index.tsx と同じ導出
  const trucksForFilter = useMemo(
    () => (trucksQuery.data ?? []).map((t) => ({ id: t.id, name: t.truckNumber || t.licensePlateValue })),
    [trucksQuery.data],
  );
  const driversForFilter = useMemo(
    () => (driversQuery.data ?? []).map((d) => ({ id: d.id, name: d.name })),
    [driversQuery.data],
  );
  const customInputFieldNames = useMemo(
    () => licenseContext.config?.custom_input_fields ?? [],
    [licenseContext.config],
  );

  return (
    <LongHaulPresenter
      startOn={startOn}
      endOn={endOn}
      deliveriesQuery={deliveriesQuery}
      legRunsQuery={legRunsQuery}
      trucks={trucksQuery.data ?? []}
      drivers={driversQuery.data ?? []}
      customInputFieldNames={customInputFieldNames}
      trucksForFilter={trucksForFilter}
      driversForFilter={driversForFilter}
      companyId={String(licenseContext.config?.selected_company_id ?? 0)}
      selectedCompanyId={licenseContext.config ? licenseContext.config.selected_company_id : null}
      isLicenseReady={Boolean(licenseContext.config)}
      isMastersReady={trucksQuery.isSuccess && driversQuery.isSuccess}
      isMastersError={trucksQuery.isError || driversQuery.isError}
      deliveryListGrouping={licenseContext.config?.delivery_list_grouping ?? 'groupByAddress'}
      useAlgorithmPlanning={Boolean(licenseContext.config?.use_algorithm_planning)}
      onNavigate={(path) => { void navigate(path); }}
      onDateRangeChange={(newStartOn, newEndOn) => {
        void navigate(`/long-haul/${newStartOn}/${newEndOn}`);
      }}
    />
  );
});

export default LongHaul;
